import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.css';
import 'survey-creator-core/survey-creator-core.css';
import './survey-form-screen.scss';
import { LoadingSpinner, SVGS } from '@zonofi/zono-money-design-system';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  getCustomerInvite,
  useAppSelector,
  useContinueChatMutation,
  useLazyGetSurveyFormAnswerByIdQuery,
  useLazyGetSurveyFormByIdQuery,
  useStartBotSessionMutation,
  useSubmitSurveyFormMutation,
} from '@zonofi/zono-money-store';
import { SurveySuccessful } from './survey-successful';
import { FormJson } from '@zonofi/zono-money-store';

export const SurveyFormScreen: React.FC = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();

  const inviteData = useAppSelector(getCustomerInvite);
  const workspaceId = inviteData?.seller?.workspaceId;
  const customerId = inviteData?.inviteId;

  const [formJson, setFormJson] = useState<FormJson>();
  const [survey, setSurvey] = useState<Model>();

  const { formId } = params;
  const formAnswerId = searchParams.get('formAnswerId');
  const waConfigId = searchParams.get('waConfigId');
  const typebotId = searchParams.get('typebotId');

  const [fetchSurveyFormAPI, fetchSurveyFormAPIResponse] =
    useLazyGetSurveyFormByIdQuery();
  const [submitSurveyFormAPI, submitSurveyFormAPIResponse] =
    useSubmitSurveyFormMutation();
  const [fetchSurveyAnswerByIdAPI, fetchSurveyAnswerByIdAPIResponse] =
    useLazyGetSurveyFormAnswerByIdQuery();
  const [continueChatAPI] = useContinueChatMutation();
  const [startBotSessionAPI] = useStartBotSessionMutation();

  const formNameTitleMap = (formJson) => {
    let res = {};

    formJson?.pages?.forEach((page) => {
      page?.elements?.forEach((element) => {
        res[element?.title] = element?.name;
      });
    });

    return res;
  };

  const prefilledAnswers = (formJson, formAnswers) => {
    let inputTitles: string[] = [];

    formJson?.pages?.forEach((page) => {
      page?.elements?.forEach((element) => {
        inputTitles?.push(element?.title);
      });
    });

    let answers = {};

    inputTitles?.forEach((inputTitle) => {
      answers[formNameTitleMap(formJson)?.[inputTitle]] =
        formAnswers?.answers?.[inputTitle];
    });

    return answers;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchSurveyFormAPI({
          formId,
          params: { sellerWorkspaceId: workspaceId, customerId },
        }).unwrap();

        setFormJson(response.formJson);

        if (formAnswerId) {
          const answerRes = await fetchSurveyAnswerByIdAPI({
            formAnswerId,
            params: { sellerWorkspaceId: workspaceId },
          }).unwrap();
          console.log('answerRes: ', answerRes);

          const model = new Model(response.formJson);
          model.showCompletedPage = false;
          model.showTitle = false;
          model.mode = 'display';
          let data = prefilledAnswers(response.formJson, answerRes);
          console.log('setting answer model: ', model);
          model.data = data;
          setSurvey(model);
        } else {
          const model = new Model(response.formJson);
          model.showCompletedPage = false;
          model.showTitle = false;
          console.log('setting edit model: ', model);
          setSurvey(model);
        }
      } catch (err) {
        console.error('Error loading form:', err);
      }
    };

    loadData();
  }, [formId, formAnswerId, workspaceId, customerId]);

  useEffect(() => {
    if (!survey) return;

    const handleComplete = (sender: any, options: any) => {
      console.log('typeof:', typeof sender.data);
      options.showSaveInProgress();
      submitSurveyFormAPI({
        formId: formId,
        params: {
          sellerWorkspaceId: workspaceId,
        },
        body: {
          customerId: customerId,
          typebotId: '',
          resultId: '',
          answers: sender.data,
        },
      });
    };

    survey.onComplete.add(handleComplete);

    return () => {
      survey.onComplete.remove(handleComplete);
    };
  }, [survey]);

  useEffect(() => {
    if (submitSurveyFormAPIResponse?.isSuccess) {
      if (inviteData?.buyer?.waSessionId) {
        continueChatAPI({
          sessionId: inviteData?.buyer?.waSessionId,
          params: {
            sellerWorkspaceId: workspaceId,
          },
          body: {
            prefilledVariables: {
              formAnswerId: submitSurveyFormAPIResponse?.data?.id,
              ...submitSurveyFormAPIResponse?.originalArgs?.body?.answers,
            },
          },
        });
      }
      if (waConfigId && typebotId) {
        startBotSessionAPI({
          params: {
            sellerWorkspaceId: workspaceId,
          },
          body: {
            typebotId,
            phone: inviteData?.buyer?.phone,
            workspaceId,
            waConfigId,
            bot: {
              prefilledVariables:
                submitSurveyFormAPIResponse?.originalArgs?.body?.answers,
            },
          },
        });
      }
    }
  }, [submitSurveyFormAPIResponse, inviteData]);

  useEffect(() => {
    fetchSurveyFormAPI({
      formId: formId,
      params: {
        sellerWorkspaceId: workspaceId,
        customerId: customerId,
      },
    });
  }, [formId]);

  return (
    <div className="survey-form-screen font-primary">
      {workspaceId === 'a6bc38da-6dcc-45a2-8d09-b3ab6fe5cc49' ? null : (
        <div className="survey-form-screen-header">
          <div className="catalog-header-seller">
            <div className="catalog-header-seller-logo-container">
              {inviteData?.seller?.profileImg ? (
                <img
                  src={inviteData?.seller?.profileImg}
                  className="catalog-header-seller-logo-container-img"
                />
              ) : (
                <img src={SVGS.UserProfileIcon} />
              )}
            </div>
            <div className="catalog-header-seller-info">
              <span className="catalog-header-seller-info-name font-primary-semibold">
                {inviteData?.seller?.name}
              </span>
              <span className="catalog-header-seller-info-subtitle font-primary">
                {fetchSurveyFormAPIResponse?.data?.title}
              </span>
            </div>
          </div>
          <div />
        </div>
      )}
      <div className="survey-form-screen-body">
        {fetchSurveyFormAPIResponse?.isLoading ||
        fetchSurveyFormAPIResponse?.isFetching ||
        submitSurveyFormAPIResponse?.isLoading ||
        !survey ? (
          <LoadingSpinner />
        ) : (fetchSurveyFormAPIResponse?.isSuccess &&
            Object.keys(formJson)?.length === 0) ||
          fetchSurveyFormAPIResponse?.isError ? (
          <span className="">Something went wrong!</span>
        ) : submitSurveyFormAPIResponse?.isSuccess ? (
          <SurveySuccessful />
        ) : (
          <Survey model={survey} />
        )}
      </div>
    </div>
  );
};
